import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguageContext';
import { MapPinIcon, CalendarIcon, StairsIcon, CheckIcon, PhoneIcon } from './Icons';
// Tampa metro area valid cities for address validation
const TAMPA_AREA_CITIES = ['tampa', 'brandon', 'riverview', 'clearwater', 'st. petersburg', 'st petersburg', 'saint petersburg', 'wesley chapel', 'lutz', "land o' lakes", 'land o lakes', 'plant city', 'lakeland', 'largo', 'dunedin', 'palm harbor', 'new port richey', 'spring hill', 'bradenton', 'sarasota', 'temple terrace', 'valrico', 'ruskin', 'sun city center', 'apollo beach', 'gibsonton', 'seffner', 'dover', 'thonotosassa', 'zephyrhills', 'dade city', 'tarpon springs', 'safety harbor', 'oldsmar', "town 'n' country", 'town n country', 'citrus park', 'carrollwood', 'northdale', 'egypt lake-leto', 'egypt lake leto', 'palmetto', 'ellenton', 'wimauma', 'lithia', 'fish hawk', 'fishhawk', 'bloomingdale', 'seminole', 'pinellas park', 'kenneth city', 'treasure island', 'madeira beach', 'indian rocks beach', 'belleair', 'odessa', 'holiday', 'hudson', 'port richey', 'bayonet point', 'trinity', 'pasco', 'hillsborough', 'pinellas', 'manatee', 'polk', 'hernando', 'kissimmee', 'winter haven', 'bartow', 'auburndale', 'haines city', 'davenport', 'clermont', 'gulfport', 'south tampa', 'ybor city', 'westchase', 'town and country', 'bay pines', 'st pete beach', 'indian shores', 'redington beach', 'north redington beach', 'redington shores', 'tierra verde', 'pass-a-grille', 'anna maria', 'holmes beach', 'longboat key', 'siesta key', 'lido key', 'osprey', 'nokomis', 'venice', 'north port', 'englewood'];
// Valid FL zip code prefixes for Tampa metro area
const TAMPA_ZIP_PREFIXES = ['335', '336', '337', '338', '346', '347', '342', '338', '339', '340', '341', '336', '337', '338', '346', '347', '335', '336', '337', '338', '339', '334', '335', '336', '337', '338', '339', '346', '347'];
// Cities that are NOT in Tampa area but might match loosely
const EXCLUDED_CITIES = ['miami', 'fort lauderdale', 'jacksonville', 'orlando', 'tallahassee', 'gainesville', 'pensacola', 'fort myers', 'naples', 'cape coral', 'west palm beach', 'boca raton', 'pompano', 'hialeah', 'hollywood, fl', 'daytona', 'ocala', 'panama city', 'key west', 'stuart', 'vero beach', 'port st lucie', 'port saint lucie', 'melbourne', 'titusville', 'cocoa'];
function isInTampaArea(address: string): boolean {
  const lower = address.toLowerCase();
  // First check if it contains an excluded city (outside Tampa area)
  const isExcluded = EXCLUDED_CITIES.some(city => lower.includes(city));
  if (isExcluded) return false;
  // Check if any known Tampa metro city name is in the address
  const cityMatch = TAMPA_AREA_CITIES.some(city => lower.includes(city));
  if (cityMatch) return true;
  // Check FL zip codes in Tampa metro range
  const zipMatch = lower.match(/\b(3[3-4]\d{3})\b/);
  if (zipMatch) {
    const prefix = zipMatch[1].substring(0, 3);
    return TAMPA_ZIP_PREFIXES.includes(prefix);
  }
  return false;
}
// ── Display-formatted cities for autocomplete suggestions ──
const TAMPA_CITIES_DISPLAY = [{
  city: 'Tampa',
  full: 'Tampa, FL'
}, {
  city: 'South Tampa',
  full: 'South Tampa, FL'
}, {
  city: 'Ybor City',
  full: 'Ybor City, Tampa, FL'
}, {
  city: 'Brandon',
  full: 'Brandon, FL'
}, {
  city: 'Riverview',
  full: 'Riverview, FL'
}, {
  city: 'Clearwater',
  full: 'Clearwater, FL'
}, {
  city: 'St. Petersburg',
  full: 'St. Petersburg, FL'
}, {
  city: 'Wesley Chapel',
  full: 'Wesley Chapel, FL'
}, {
  city: 'Lutz',
  full: 'Lutz, FL'
}, {
  city: "Land O' Lakes",
  full: "Land O' Lakes, FL"
}, {
  city: 'Plant City',
  full: 'Plant City, FL'
}, {
  city: 'Lakeland',
  full: 'Lakeland, FL'
}, {
  city: 'Largo',
  full: 'Largo, FL'
}, {
  city: 'Dunedin',
  full: 'Dunedin, FL'
}, {
  city: 'Palm Harbor',
  full: 'Palm Harbor, FL'
}, {
  city: 'New Port Richey',
  full: 'New Port Richey, FL'
}, {
  city: 'Spring Hill',
  full: 'Spring Hill, FL'
}, {
  city: 'Bradenton',
  full: 'Bradenton, FL'
}, {
  city: 'Sarasota',
  full: 'Sarasota, FL'
}, {
  city: 'Temple Terrace',
  full: 'Temple Terrace, FL'
}, {
  city: 'Valrico',
  full: 'Valrico, FL'
}, {
  city: 'Ruskin',
  full: 'Ruskin, FL'
}, {
  city: 'Sun City Center',
  full: 'Sun City Center, FL'
}, {
  city: 'Apollo Beach',
  full: 'Apollo Beach, FL'
}, {
  city: 'Gibsonton',
  full: 'Gibsonton, FL'
}, {
  city: 'Seffner',
  full: 'Seffner, FL'
}, {
  city: 'Dover',
  full: 'Dover, FL'
}, {
  city: 'Thonotosassa',
  full: 'Thonotosassa, FL'
}, {
  city: 'Zephyrhills',
  full: 'Zephyrhills, FL'
}, {
  city: 'Dade City',
  full: 'Dade City, FL'
}, {
  city: 'Tarpon Springs',
  full: 'Tarpon Springs, FL'
}, {
  city: 'Safety Harbor',
  full: 'Safety Harbor, FL'
}, {
  city: 'Oldsmar',
  full: 'Oldsmar, FL'
}, {
  city: 'Citrus Park',
  full: 'Citrus Park, FL'
}, {
  city: 'Carrollwood',
  full: 'Carrollwood, FL'
}, {
  city: 'Westchase',
  full: 'Westchase, FL'
}, {
  city: 'Palmetto',
  full: 'Palmetto, FL'
}, {
  city: 'Ellenton',
  full: 'Ellenton, FL'
}, {
  city: 'Wimauma',
  full: 'Wimauma, FL'
}, {
  city: 'Lithia',
  full: 'Lithia, FL'
}, {
  city: 'FishHawk',
  full: 'FishHawk, FL'
}, {
  city: 'Bloomingdale',
  full: 'Bloomingdale, FL'
}, {
  city: 'Seminole',
  full: 'Seminole, FL'
}, {
  city: 'Pinellas Park',
  full: 'Pinellas Park, FL'
}, {
  city: 'Treasure Island',
  full: 'Treasure Island, FL'
}, {
  city: 'Madeira Beach',
  full: 'Madeira Beach, FL'
}, {
  city: 'Odessa',
  full: 'Odessa, FL'
}, {
  city: 'Holiday',
  full: 'Holiday, FL'
}, {
  city: 'Hudson',
  full: 'Hudson, FL'
}, {
  city: 'Port Richey',
  full: 'Port Richey, FL'
}, {
  city: 'Trinity',
  full: 'Trinity, FL'
}, {
  city: 'Kissimmee',
  full: 'Kissimmee, FL'
}, {
  city: 'Winter Haven',
  full: 'Winter Haven, FL'
}, {
  city: 'Bartow',
  full: 'Bartow, FL'
}, {
  city: 'Auburndale',
  full: 'Auburndale, FL'
}, {
  city: 'Haines City',
  full: 'Haines City, FL'
}, {
  city: 'Davenport',
  full: 'Davenport, FL'
}, {
  city: 'Clermont',
  full: 'Clermont, FL'
}, {
  city: 'Gulfport',
  full: 'Gulfport, FL'
}, {
  city: 'Venice',
  full: 'Venice, FL'
}, {
  city: 'North Port',
  full: 'North Port, FL'
}, {
  city: 'Englewood',
  full: 'Englewood, FL'
}, {
  city: 'St Pete Beach',
  full: 'St Pete Beach, FL'
}, {
  city: 'Anna Maria',
  full: 'Anna Maria, FL'
}, {
  city: 'Holmes Beach',
  full: 'Holmes Beach, FL'
}, {
  city: 'Longboat Key',
  full: 'Longboat Key, FL'
}, {
  city: 'Siesta Key',
  full: 'Siesta Key, FL'
}];
// ── Address Autocomplete Component ──────────────────────────
interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  isValid: boolean;
  hasError: boolean;
  errorMessage?: string;
  successMessage?: string;
  borderValidClass: string;
}
function AddressAutocomplete({
  value,
  onChange,
  placeholder,
  isValid,
  hasError,
  errorMessage,
  successMessage,
  borderValidClass
}: AddressAutocompleteProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Filter suggestions based on input
  const query = value.toLowerCase().trim();
  const suggestions = query.length >= 1 ? TAMPA_CITIES_DISPLAY.filter(item => item.city.toLowerCase().includes(query) || item.full.toLowerCase().includes(query)).slice(0, 8) : [];
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  // Reset highlight when suggestions change
  useEffect(() => {
    setHighlightIndex(-1);
  }, [value]);
  const handleSelect = (cityFull: string) => {
    onChange(cityFull);
    setShowSuggestions(false);
    inputRef.current?.blur();
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex(prev => prev < suggestions.length - 1 ? prev + 1 : 0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex(prev => prev > 0 ? prev - 1 : suggestions.length - 1);
    } else if (e.key === 'Enter' && highlightIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[highlightIndex].full);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };
  // Highlight matching text in suggestion
  const highlightMatch = (text: string) => {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query);
    if (idx === -1) return text;
    return <>
        {text.slice(0, idx)}
        <span className="text-brand-yellow font-bold" data-id="element-591">
          {text.slice(idx, idx + query.length)}
        </span>
        {text.slice(idx + query.length)}
      </>;
  };
  const inputClass = `w-full bg-white/5 border rounded-lg px-4 py-3 pr-10 text-white focus:outline-none focus:ring-1 transition-colors ${hasError ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : isValid ? 'border-green-500 focus:border-green-500 focus:ring-green-500' : borderValidClass}`;
  return <div ref={wrapperRef} className="relative" data-id="element-592">
      <div className="relative" data-id="element-593">
        <input ref={inputRef} type="text" value={value} onChange={e => {
        onChange(e.target.value);
        setShowSuggestions(true);
      }} onFocus={() => {
        if (suggestions.length > 0) setShowSuggestions(true);
      }} onKeyDown={handleKeyDown} className={inputClass} placeholder={placeholder} autoComplete="off" data-id="element-594" />
        {/* Right icon: checkmark or search */}
        {isValid ? <div className="absolute right-3 top-1/2 -translate-y-1/2" data-id="element-595">
            <CheckIcon className="w-5 h-5 text-green-500" data-id="element-596" />
          </div> : <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-597">
            <MapPinIcon className="w-4 h-4 text-gray-500" data-id="element-598" />
          </div>}
      </div>

      {/* Suggestions dropdown */}
      <AnimatePresence data-id="element-599">
        {showSuggestions && suggestions.length > 0 && !isValid && <motion.div initial={{
        opacity: 0,
        y: -4,
        scaleY: 0.95
      }} animate={{
        opacity: 1,
        y: 0,
        scaleY: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scaleY: 0.95
      }} transition={{
        duration: 0.15
      }} className="absolute z-[60] w-full mt-1 bg-[#141414] border border-white/15 rounded-xl shadow-2xl overflow-hidden origin-top" data-id="element-600">
            <div className="py-1 max-h-[240px] overflow-y-auto" data-id="element-601">
              <div className="px-3 py-1.5 border-b border-white/5" data-id="element-602">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold" data-id="element-603">
                  Tampa Service Area
                </span>
              </div>
              {suggestions.map((item, idx) => <button key={item.full} type="button" onClick={() => handleSelect(item.full)} onMouseEnter={() => setHighlightIndex(idx)} className={`w-full text-left px-3 py-2.5 flex items-center gap-3 transition-colors ${idx === highlightIndex ? 'bg-brand-red/15 text-white' : 'text-gray-300 hover:bg-white/5'}`} data-id="element-604">
                  <MapPinIcon className={`w-4 h-4 flex-shrink-0 ${idx === highlightIndex ? 'text-brand-red' : 'text-gray-600'}`} data-id="element-605" />
                  <div className="flex-1 min-w-0" data-id="element-606">
                    <span className="text-sm truncate block" data-id="element-607">
                      {highlightMatch(item.full)}
                    </span>
                  </div>
                  <span className="text-[10px] text-green-500/60 flex-shrink-0" data-id="element-608">
                    ✓ 50mi
                  </span>
                </button>)}
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Success message */}
      {isValid && !hasError && successMessage && <p className="text-green-400 text-xs flex items-center gap-1 mt-2" data-id="element-609">
          <CheckIcon className="w-3 h-3" data-id="element-610" />
          {successMessage}
        </p>}

      {/* Error message */}
      {hasError && errorMessage && <p className="text-red-400 text-xs flex items-center gap-1 mt-2" data-id="element-611">
          {errorMessage}
        </p>}
    </div>;
}
// ── Custom Mini Calendar ──────────────────────────────────
interface MiniCalendarProps {
  selectedDate: string;
  minDate: Date;
  onSelect: (dateStr: string) => void;
}
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
function MiniCalendar({
  selectedDate,
  minDate,
  onSelect
}: MiniCalendarProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const initialMonth = selectedDate ? new Date(selectedDate + 'T00:00:00') : new Date(minDate);
  const [viewYear, setViewYear] = useState(initialMonth.getFullYear());
  const [viewMonth, setViewMonth] = useState(initialMonth.getMonth());
  // Normalize minDate to midnight for comparison
  const minDateNorm = new Date(minDate);
  minDateNorm.setHours(0, 0, 0, 0);
  // Use numeric comparison to avoid timezone issues
  const todayNum = today.getFullYear() * 10000 + (today.getMonth() + 1) * 100 + today.getDate();
  const minDateNum = minDateNorm.getFullYear() * 10000 + (minDateNorm.getMonth() + 1) * 100 + minDateNorm.getDate();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDayOfWeek = new Date(viewYear, viewMonth, 1).getDay();
  const prevMonth = () => {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear(viewYear - 1);
    } else {
      setViewMonth(viewMonth - 1);
    }
  };
  const nextMonth = () => {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };
  const toDateStr = (y: number, m: number, d: number) => {
    return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  };
  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return <div className="bg-white/5 border border-white/10 rounded-xl p-4" data-id="element-612">
      {/* Month navigation */}
      <div className="flex items-center justify-between mb-4" data-id="element-613">
        <button type="button" onClick={prevMonth} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors" data-id="element-614">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" data-id="element-615">
            <polyline points="15 18 9 12 15 6" data-id="element-616" />
          </svg>
        </button>
        <span className="font-oswald text-white font-bold tracking-wide" data-id="element-617">
          {MONTH_NAMES[viewMonth]} {viewYear}
        </span>
        <button type="button" onClick={nextMonth} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors" data-id="element-618">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4" data-id="element-619">
            <polyline points="9 18 15 12 9 6" data-id="element-620" />
          </svg>
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-2" data-id="element-621">
        {WEEKDAYS.map(wd => <div key={wd} className="text-center text-[10px] font-bold text-gray-500 uppercase tracking-wider py-1" data-id="element-622">
            {wd}
          </div>)}
      </div>

      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1" data-id="element-623">
        {cells.map((day, idx) => {
        if (day === null) {
          return <div key={`empty-${idx}`} className="h-9" data-id="element-624" />;
        }
        const cellNum = viewYear * 10000 + (viewMonth + 1) * 100 + day;
        const dateStr = toDateStr(viewYear, viewMonth, day);
        const isSelected = dateStr === selectedDate;
        const isToday = cellNum === todayNum;
        const isPast = cellNum < todayNum;
        const isTooSoon = cellNum < minDateNum;
        const isUnavailable = isPast || isTooSoon;
        const isAvailable = !isUnavailable;
        return <button key={dateStr} type="button" disabled={isUnavailable} onClick={() => isAvailable && onSelect(dateStr)} className={`h-9 rounded-lg text-sm font-medium transition-all relative flex items-center justify-center ${isSelected ? 'bg-green-600 text-white font-bold shadow-[0_0_12px_rgba(34,197,94,0.4)]' : isUnavailable ? 'text-red-900/60 bg-red-500/10 cursor-not-allowed line-through' : isToday ? 'bg-green-900/30 text-green-400 font-bold ring-1 ring-green-500' : 'text-green-300 hover:bg-green-600/20 hover:text-green-100 cursor-pointer bg-green-500/5'}`} title={isUnavailable ? 'Not available — minimum 72h advance booking required' : ''} data-id="element-625">
              {day}
            </button>;
      })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 pt-3 border-t border-white/5" data-id="element-626">
        <div className="flex items-center gap-1.5" data-id="element-627">
          <div className="w-3 h-3 rounded bg-green-500/10 border border-green-500/30" data-id="element-628"></div>
          <span className="text-[10px] text-gray-500" data-id="element-629">Available</span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-630">
          <div className="w-3 h-3 rounded bg-red-500/10 border border-red-900/30" data-id="element-631"></div>
          <span className="text-[10px] text-gray-500" data-id="element-632">Unavailable</span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-633">
          <div className="w-3 h-3 rounded bg-green-600" data-id="element-634"></div>
          <span className="text-[10px] text-gray-500" data-id="element-635">Selected</span>
        </div>
        <div className="flex items-center gap-1.5" data-id="element-636">
          <div className="w-3 h-3 rounded bg-green-900/30 ring-1 ring-green-500" data-id="element-637"></div>
          <span className="text-[10px] text-gray-500" data-id="element-638">Today</span>
        </div>
      </div>
    </div>;
}
interface OrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPackage: any | null;
}
type Step = 'details' | 'contact' | 'summary' | 'success';
// Company email for order notifications
const COMPANY_EMAIL = 'orders@movefighters.com';
export const OrderModal: React.FC<OrderModalProps> = ({
  isOpen,
  onClose,
  selectedPackage
}) => {
  const {
    t
  } = useLanguage();
  const [step, setStep] = useState<Step>('details');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Form state
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [moveDate, setMoveDate] = useState('');
  const [originStairs, setOriginStairs] = useState('ground');
  const [destStairs, setDestStairs] = useState('ground');
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Real-time address validation status
  const originValid = origin.trim().length > 2 && isInTampaArea(origin);
  const originInvalid = origin.trim().length > 2 && !isInTampaArea(origin);
  const destValid = destination.trim().length > 2 && isInTampaArea(destination);
  const destInvalid = destination.trim().length > 2 && !isInTampaArea(destination);
  // Min date (72 hours from now)
  const minDate = new Date();
  minDate.setHours(minDate.getHours() + 72);
  const minDateString = minDate.toISOString().split('T')[0];
  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setStep('details');
        setOrigin('');
        setDestination('');
        setMoveDate('');
        setOriginStairs('ground');
        setDestStairs('ground');
        setFullName('');
        setEmail('');
        setPhone('');
        setNotes('');
        setErrors({});
      }, 300);
    }
  }, [isOpen]);
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  const validateDetails = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!origin.trim()) {
      newErrors.origin = t.order.errors.required;
    } else if (!isInTampaArea(origin)) {
      newErrors.origin = t.order.errors.outsideArea;
    }
    if (!destination.trim()) {
      newErrors.destination = t.order.errors.required;
    } else if (!isInTampaArea(destination)) {
      newErrors.destination = t.order.errors.outsideArea;
    }
    if (!moveDate) {
      newErrors.moveDate = t.order.errors.required;
    } else {
      const selected = new Date(moveDate + 'T00:00:00');
      const minNorm = new Date(minDate);
      minNorm.setHours(0, 0, 0, 0);
      if (selected < minNorm) {
        newErrors.moveDate = t.order.errors.minDate;
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const validateContact = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!fullName.trim()) newErrors.fullName = t.order.errors.required;
    if (!email.trim()) {
      newErrors.email = t.order.errors.required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = t.order.errors.invalidEmail;
    }
    if (!phone.trim()) {
      newErrors.phone = t.order.errors.required;
    } else if (phone.replace(/\D/g, '').length < 10) {
      newErrors.phone = t.order.errors.invalidPhone;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleNextFromDetails = () => {
    if (validateDetails()) setStep('contact');
  };
  const handleNextFromContact = () => {
    if (validateContact()) setStep('summary');
  };
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  const handleConfirmOrder = () => {
    setIsSubmitting(true);
    // Build order details for email
    const orderDetails = {
      package: selectedPackage?.name,
      packageDesc: selectedPackage?.desc,
      price: `$${selectedPackage?.price}`,
      customer: fullName,
      email: email,
      phone: phone,
      origin: origin,
      originStairs: originStairs,
      destination: destination,
      destStairs: destStairs,
      moveDate: moveDate,
      notes: notes || 'None',
      orderDate: new Date().toISOString()
    };
    // Build mailto link as fallback / primary method
    const subject = encodeURIComponent(`NEW ORDER: ${selectedPackage?.name} - ${fullName} - ${moveDate}`);
    const body = encodeURIComponent(`--- NEW MOVE FIGHTERS ORDER ---\n\n` + `═══════════════════════════\n` + `PACKAGE: ${orderDetails.package} ${orderDetails.packageDesc}\n` + `PRICE: ${orderDetails.price}\n` + `═══════════════════════════\n\n` + `CUSTOMER INFORMATION:\n` + `Name: ${orderDetails.customer}\n` + `Email: ${orderDetails.email}\n` + `Phone: ${orderDetails.phone}\n\n` + `MOVE DETAILS:\n` + `Origin: ${orderDetails.origin}\n` + `Origin Access: ${orderDetails.originStairs}\n` + `Destination: ${orderDetails.destination}\n` + `Destination Access: ${orderDetails.destStairs}\n` + `Move Date: ${orderDetails.moveDate}\n\n` + `SPECIAL NOTES:\n${orderDetails.notes}\n\n` + `Order placed: ${orderDetails.orderDate}\n` + `═══════════════════════════`);
    // Store order in localStorage for reference
    const existingOrders = JSON.parse(localStorage.getItem('mf_orders') || '[]');
    existingOrders.push(orderDetails);
    localStorage.setItem('mf_orders', JSON.stringify(existingOrders));
    // Open mailto to send to company
    const mailtoLink = `mailto:${COMPANY_EMAIL}?subject=${subject}&body=${body}`;
    // Simulate processing delay then show success
    setTimeout(() => {
      // Try to open email client
      window.open(mailtoLink, '_blank');
      setIsSubmitting(false);
      setStep('success');
    }, 1500);
  };
  const stairsLabel = (value: string) => {
    const labels: Record<string, string> = {
      ground: t.order.stairsOptions.ground,
      elevator: t.order.stairsOptions.elevator,
      stairs1: t.order.stairsOptions.stairs1,
      stairs2: t.order.stairsOptions.stairs2,
      stairs3: t.order.stairsOptions.stairs3
    };
    return labels[value] || value;
  };
  if (!isOpen || !selectedPackage) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-639">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80" style={{
      zIndex: 1
    }} onClick={onClose} data-id="element-640" />

      {/* Modal */}
      <div className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-brand-dark border border-white/10 rounded-2xl shadow-2xl" style={{
      zIndex: 2
    }} data-id="element-641">
        {/* Close button */}
        <button onClick={onClose} className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors" data-id="element-642">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" data-id="element-643">
            <line x1="18" y1="6" x2="6" y2="18" data-id="element-644" />
            <line x1="6" y1="6" x2="18" y2="18" data-id="element-645" />
          </svg>
        </button>

        {/* Package header */}
        <div className="bg-brand-black p-6 border-b border-white/10" data-id="element-646">
          <div className="flex items-center justify-between pr-10" data-id="element-647">
            <div data-id="element-648">
              <p className="text-brand-red text-sm font-bold uppercase tracking-wider" data-id="element-649">
                {t.order.yourPackage}
              </p>
              <h3 className="font-oswald text-2xl font-bold text-white uppercase mt-1" data-id="element-650">
                {selectedPackage.name}
              </h3>
              <p className="text-gray-400 text-sm" data-id="element-651">{selectedPackage.desc}</p>
            </div>
            <div className="text-right" data-id="element-652">
              <span className="font-oswald text-4xl font-bold text-white" data-id="element-653">
                ${selectedPackage.price}
              </span>
              <p className="text-gray-500 text-xs mt-1" data-id="element-654">{t.order.totalFixed}</p>
            </div>
          </div>

          {/* Step indicator */}
          {step !== 'success' && <div className="flex items-center gap-2 mt-6" data-id="element-655">
              {['details', 'contact', 'summary'].map((s, i) => <Fragment key={s} data-id="element-656">
                  <div className={`flex items-center gap-2 ${step === s ? 'text-brand-red' : ['details', 'contact', 'summary'].indexOf(step) > i ? 'text-green-500' : 'text-gray-600'}`} data-id="element-657">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 ${step === s ? 'border-brand-red bg-brand-red/10 text-brand-red' : ['details', 'contact', 'summary'].indexOf(step) > i ? 'border-green-500 bg-green-500/10 text-green-500' : 'border-gray-600 text-gray-600'}`} data-id="element-658">
                      {['details', 'contact', 'summary'].indexOf(step) > i ? <CheckIcon className="w-4 h-4" data-id="element-659" /> : i + 1}
                    </div>
                    <span className="text-xs font-medium hidden sm:block" data-id="element-660">
                      {i === 0 ? t.order.stepDetails : i === 1 ? t.order.stepContact : t.order.stepSummary}
                    </span>
                  </div>
                  {i < 2 && <div className={`flex-1 h-0.5 ${['details', 'contact', 'summary'].indexOf(step) > i ? 'bg-green-500' : 'bg-gray-700'}`} data-id="element-661" />}
                </Fragment>)}
            </div>}
        </div>

        {/* Step content */}
        <div className="p-6" data-id="element-662">
          <AnimatePresence mode="wait" data-id="element-663">
            {/* STEP 1: Move Details */}
            {step === 'details' && <motion.div key="details" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-5" data-id="element-664">
                <h4 className="font-oswald text-xl font-bold text-white uppercase" data-id="element-665">
                  {t.order.moveDetails}
                </h4>

                {/* Origin */}
                <div className="space-y-2" data-id="element-666">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2" data-id="element-667">
                    <MapPinIcon className="w-4 h-4 text-brand-red" data-id="element-668" />
                    {t.order.origin}
                  </label>
                  <AddressAutocomplete value={origin} onChange={val => {
                setOrigin(val);
                setErrors(prev => ({
                  ...prev,
                  origin: ''
                }));
              }} placeholder={t.order.originPlaceholder} isValid={originValid} hasError={!!errors.origin} errorMessage={errors.origin} successMessage={t.order.areaQualifies || 'Area qualifies — within Tampa 50-mile service zone'} borderValidClass="border-white/10 focus:border-brand-red focus:ring-brand-red" data-id="element-669" />
                </div>

                {/* Origin stairs */}
                <div className="space-y-2" data-id="element-670">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2" data-id="element-671">
                    <StairsIcon className="w-4 h-4 text-gray-400" data-id="element-672" />
                    {t.order.originAccess}
                  </label>
                  <select value={originStairs} onChange={e => setOriginStairs(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors appearance-none" data-id="element-673">
                    <option value="ground" className="bg-brand-black" data-id="element-674">
                      {t.order.stairsOptions.ground}
                    </option>
                    <option value="elevator" className="bg-brand-black" data-id="element-675">
                      {t.order.stairsOptions.elevator}
                    </option>
                    <option value="stairs1" className="bg-brand-black" data-id="element-676">
                      {t.order.stairsOptions.stairs1}
                    </option>
                    <option value="stairs2" className="bg-brand-black" data-id="element-677">
                      {t.order.stairsOptions.stairs2}
                    </option>
                    <option value="stairs3" className="bg-brand-black" data-id="element-678">
                      {t.order.stairsOptions.stairs3}
                    </option>
                  </select>
                </div>

                {/* Destination */}
                <div className="space-y-2" data-id="element-679">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2" data-id="element-680">
                    <MapPinIcon className="w-4 h-4 text-brand-yellow" data-id="element-681" />
                    {t.order.destination}
                  </label>
                  <AddressAutocomplete value={destination} onChange={val => {
                setDestination(val);
                setErrors(prev => ({
                  ...prev,
                  destination: ''
                }));
              }} placeholder={t.order.destPlaceholder} isValid={destValid} hasError={!!errors.destination} errorMessage={errors.destination} successMessage={t.order.areaQualifies || 'Area qualifies — within Tampa 50-mile service zone'} borderValidClass="border-white/10 focus:border-brand-red focus:ring-brand-red" data-id="element-682" />
                </div>

                {/* Destination stairs */}
                <div className="space-y-2" data-id="element-683">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2" data-id="element-684">
                    <StairsIcon className="w-4 h-4 text-gray-400" data-id="element-685" />
                    {t.order.destAccess}
                  </label>
                  <select value={destStairs} onChange={e => setDestStairs(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors appearance-none" data-id="element-686">
                    <option value="ground" className="bg-brand-black" data-id="element-687">
                      {t.order.stairsOptions.ground}
                    </option>
                    <option value="elevator" className="bg-brand-black" data-id="element-688">
                      {t.order.stairsOptions.elevator}
                    </option>
                    <option value="stairs1" className="bg-brand-black" data-id="element-689">
                      {t.order.stairsOptions.stairs1}
                    </option>
                    <option value="stairs2" className="bg-brand-black" data-id="element-690">
                      {t.order.stairsOptions.stairs2}
                    </option>
                    <option value="stairs3" className="bg-brand-black" data-id="element-691">
                      {t.order.stairsOptions.stairs3}
                    </option>
                  </select>
                </div>

                {/* Move date — Custom Calendar */}
                <div className="space-y-2" data-id="element-692">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2" data-id="element-693">
                    <CalendarIcon className="w-4 h-4 text-brand-red" data-id="element-694" />
                    {t.order.moveDate}
                  </label>
                  <MiniCalendar selectedDate={moveDate} minDate={minDate} onSelect={dateStr => {
                setMoveDate(dateStr);
                setErrors(prev => ({
                  ...prev,
                  moveDate: ''
                }));
              }} data-id="element-695" />
                  {errors.moveDate && <p className="text-red-400 text-xs" data-id="element-696">{errors.moveDate}</p>}
                  <p className="text-gray-500 text-xs" data-id="element-697">{t.order.minDateNote}</p>
                </div>

                <button onClick={handleNextFromDetails} className="w-full bg-brand-red hover:bg-brand-darkred text-white py-4 rounded-lg font-oswald font-bold text-lg tracking-wider uppercase transition-all transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-698">
                  {t.order.next}
                </button>
              </motion.div>}

            {/* STEP 2: Contact Info */}
            {step === 'contact' && <motion.div key="contact" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-5" data-id="element-699">
                <h4 className="font-oswald text-xl font-bold text-white uppercase" data-id="element-700">
                  {t.order.contactInfo}
                </h4>

                <div className="space-y-2" data-id="element-701">
                  <label className="text-sm font-medium text-gray-300" data-id="element-702">
                    {t.order.fullName}
                  </label>
                  <input type="text" value={fullName} onChange={e => {
                setFullName(e.target.value);
                setErrors(prev => ({
                  ...prev,
                  fullName: ''
                }));
              }} className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-1 transition-colors ${errors.fullName ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-white/10 focus:border-brand-red focus:ring-brand-red'}`} placeholder="John Smith" data-id="element-703" />
                  {errors.fullName && <p className="text-red-400 text-xs" data-id="element-704">{errors.fullName}</p>}
                </div>

                <div className="space-y-2" data-id="element-705">
                  <label className="text-sm font-medium text-gray-300" data-id="element-706">
                    {t.order.email}
                  </label>
                  <input type="email" value={email} onChange={e => {
                setEmail(e.target.value);
                setErrors(prev => ({
                  ...prev,
                  email: ''
                }));
              }} className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-1 transition-colors ${errors.email ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-white/10 focus:border-brand-red focus:ring-brand-red'}`} placeholder="john@example.com" data-id="element-707" />
                  {errors.email && <p className="text-red-400 text-xs" data-id="element-708">{errors.email}</p>}
                </div>

                <div className="space-y-2" data-id="element-709">
                  <label className="text-sm font-medium text-gray-300 flex items-center gap-2" data-id="element-710">
                    <PhoneIcon className="w-4 h-4 text-brand-red" data-id="element-711" />
                    {t.order.phone}
                  </label>
                  <input type="tel" value={phone} onChange={e => {
                setPhone(e.target.value);
                setErrors(prev => ({
                  ...prev,
                  phone: ''
                }));
              }} className={`w-full bg-white/5 border rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-1 transition-colors ${errors.phone ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-white/10 focus:border-brand-red focus:ring-brand-red'}`} placeholder="(813) 555-0199" data-id="element-712" />
                  {errors.phone && <p className="text-red-400 text-xs" data-id="element-713">{errors.phone}</p>}
                </div>

                <div className="space-y-2" data-id="element-714">
                  <label className="text-sm font-medium text-gray-300" data-id="element-715">
                    {t.order.notes}
                  </label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-brand-red focus:ring-1 focus:ring-brand-red transition-colors resize-none" placeholder={t.order.notesPlaceholder} data-id="element-716" />
                </div>

                <div className="flex gap-3" data-id="element-717">
                  <button onClick={() => setStep('details')} className="flex-1 bg-white/10 hover:bg-white/20 text-white py-4 rounded-lg font-oswald font-bold text-lg tracking-wider uppercase transition-all" data-id="element-718">
                    {t.order.back}
                  </button>
                  <button onClick={handleNextFromContact} className="flex-[2] bg-brand-red hover:bg-brand-darkred text-white py-4 rounded-lg font-oswald font-bold text-lg tracking-wider uppercase transition-all transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-719">
                    {t.order.reviewOrder}
                  </button>
                </div>
              </motion.div>}

            {/* STEP 3: Order Summary */}
            {step === 'summary' && <motion.div key="summary" initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} className="space-y-6" data-id="element-720">
                <h4 className="font-oswald text-xl font-bold text-white uppercase" data-id="element-721">
                  {t.order.orderSummary}
                </h4>

                {/* Move details summary */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-4" data-id="element-722">
                  <h5 className="text-sm font-bold text-brand-red uppercase tracking-wider" data-id="element-723">
                    {t.order.moveDetails}
                  </h5>

                  <div className="space-y-3" data-id="element-724">
                    <div className="flex items-start gap-3" data-id="element-725">
                      <MapPinIcon className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" data-id="element-726" />
                      <div data-id="element-727">
                        <p className="text-xs text-gray-500 uppercase" data-id="element-728">
                          {t.order.fromLabel}
                        </p>
                        <p className="text-white text-sm" data-id="element-729">{origin}</p>
                        <p className="text-gray-500 text-xs" data-id="element-730">
                          {stairsLabel(originStairs)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-731">
                      <MapPinIcon className="w-4 h-4 text-brand-yellow flex-shrink-0 mt-0.5" data-id="element-732" />
                      <div data-id="element-733">
                        <p className="text-xs text-gray-500 uppercase" data-id="element-734">
                          {t.order.toLabel}
                        </p>
                        <p className="text-white text-sm" data-id="element-735">{destination}</p>
                        <p className="text-gray-500 text-xs" data-id="element-736">
                          {stairsLabel(destStairs)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3" data-id="element-737">
                      <CalendarIcon className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" data-id="element-738" />
                      <div data-id="element-739">
                        <p className="text-xs text-gray-500 uppercase" data-id="element-740">
                          {t.order.dateLabel}
                        </p>
                        <p className="text-white text-sm" data-id="element-741">
                          {moveDate ? formatDate(moveDate) : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact summary */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-3" data-id="element-742">
                  <h5 className="text-sm font-bold text-brand-red uppercase tracking-wider" data-id="element-743">
                    {t.order.contactInfo}
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm" data-id="element-744">
                    <div data-id="element-745">
                      <p className="text-gray-500 text-xs uppercase" data-id="element-746">
                        {t.order.fullName}
                      </p>
                      <p className="text-white" data-id="element-747">{fullName}</p>
                    </div>
                    <div data-id="element-748">
                      <p className="text-gray-500 text-xs uppercase" data-id="element-749">
                        {t.order.phone}
                      </p>
                      <p className="text-white" data-id="element-750">{phone}</p>
                    </div>
                    <div className="sm:col-span-2" data-id="element-751">
                      <p className="text-gray-500 text-xs uppercase" data-id="element-752">
                        {t.order.email}
                      </p>
                      <p className="text-white" data-id="element-753">{email}</p>
                    </div>
                    {notes && <div className="sm:col-span-2" data-id="element-754">
                        <p className="text-gray-500 text-xs uppercase" data-id="element-755">
                          {t.order.notes}
                        </p>
                        <p className="text-white" data-id="element-756">{notes}</p>
                      </div>}
                  </div>
                </div>

                {/* Price total */}
                <div className="bg-brand-red/10 border border-brand-red/30 rounded-xl p-5" data-id="element-757">
                  <div className="flex items-center justify-between" data-id="element-758">
                    <div data-id="element-759">
                      <p className="text-white font-bold" data-id="element-760">
                        {selectedPackage.name} {selectedPackage.desc}
                      </p>
                      <p className="text-gray-400 text-sm" data-id="element-761">
                        {t.order.allIncluded}
                      </p>
                    </div>
                    <span className="font-oswald text-3xl font-bold text-white" data-id="element-762">
                      ${selectedPackage.price}
                    </span>
                  </div>
                </div>

                <p className="text-gray-500 text-xs text-center" data-id="element-763">
                  {t.order.emailNotice}
                </p>

                <div className="flex gap-3" data-id="element-764">
                  <button onClick={() => setStep('contact')} className="flex-1 bg-white/10 hover:bg-white/20 text-white py-4 rounded-lg font-oswald font-bold text-lg tracking-wider uppercase transition-all" data-id="element-765">
                    {t.order.back}
                  </button>
                  <button onClick={handleConfirmOrder} disabled={isSubmitting} className="flex-[2] bg-brand-yellow hover:bg-brand-gold text-brand-black py-4 rounded-lg font-oswald font-bold text-lg tracking-wider uppercase transition-all transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 flex items-center justify-center gap-2" data-id="element-766">
                    {isSubmitting ? <div className="w-6 h-6 border-2 border-brand-black/30 border-t-brand-black rounded-full animate-spin" data-id="element-767"></div> : t.order.confirmAndPay}
                  </button>
                </div>
              </motion.div>}

            {/* SUCCESS */}
            {step === 'success' && <motion.div key="success" initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-8 space-y-6" data-id="element-768">
                <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto" data-id="element-769">
                  <CheckIcon className="w-10 h-10 text-white" data-id="element-770" />
                </div>
                <div data-id="element-771">
                  <h3 className="font-oswald text-3xl font-bold text-white uppercase mb-2" data-id="element-772">
                    {t.order.successTitle}
                  </h3>
                  <p className="text-gray-400 max-w-md mx-auto" data-id="element-773">
                    {t.order.successMessage}
                  </p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 inline-block" data-id="element-774">
                  <p className="text-gray-500 text-xs uppercase mb-1" data-id="element-775">
                    {t.order.orderRef}
                  </p>
                  <p className="font-oswald text-xl font-bold text-brand-yellow" data-id="element-776">
                    MF-{Date.now().toString().slice(-6)}
                  </p>
                </div>
                <button onClick={onClose} className="bg-brand-red hover:bg-brand-darkred text-white px-8 py-3 rounded-lg font-oswald font-bold tracking-wider uppercase transition-all" data-id="element-777">
                  {t.order.close}
                </button>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
};